import React, { useState, useEffect } from 'react';
import { Search, Plus, Loader, Trash2, RotateCcw, AlertTriangle, Edit, X, Type, Hand } from 'lucide-react';
import { projectNamesService } from './projectNamesService';
import { AddProjectNameModal } from './AddProjectNameModal';
import { EditProjectNameModal } from './EditProjectNameModal';
import { ProjectName } from '../../types';
import { useAuth } from '../../hooks/useAuth';

export const ProjectNamesModule: React.FC = () => {
  const { user, permissions, canClaimContent, canDeleteContent } = useAuth();
  const [projectNames, setProjectNames] = useState<ProjectName[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'available' | 'claimed' | 'deleted'>('available');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState<ProjectName | null>(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [tabCounts, setTabCounts] = useState({ available: 0, claimed: 0, deleted: 0 });

  useEffect(() => {
    loadProjectNames();
  }, []);

  const loadProjectNames = async () => {
    try {
      setLoading(true);
      const fetchedProjectNames = await projectNamesService.getProjectNames();
      setProjectNames(fetchedProjectNames);
      
      // Calculate tab counts
      const counts = {
        available: fetchedProjectNames.filter(pn => !pn.claimed && !pn.deleted).length,
        claimed: fetchedProjectNames.filter(pn => pn.claimed && !pn.deleted).length,
        deleted: fetchedProjectNames.filter(pn => pn.deleted).length
      };
      setTabCounts(counts);
    } catch (error) {
      console.error('Failed to load project names:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all tags with counts (similar to logos module)
  const getAllTagsWithCounts = () => {
    const tagCounts = new Map<string, number>();
    
    // Filter project names based on current tab
    const relevantProjectNames = projectNames.filter(pn => {
      if (activeTab === 'available') return !pn.claimed && !pn.deleted;
      if (activeTab === 'claimed') return pn.claimed && !pn.deleted;
      if (activeTab === 'deleted') return pn.deleted;
      return true;
    });
    
    relevantProjectNames.forEach(projectName => {
      projectName.tags.forEach(tag => {
        if (tag.trim()) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      });
    });
    
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Get popular tags (top 10)
  const getPopularTags = () => {
    return getAllTagsWithCounts().slice(0, 10);
  };

  // Handle popular tag click
  const handlePopularTagClick = (tag: string) => {
    if (!activeFilters.includes(tag)) {
      setActiveFilters(prev => [...prev, tag]);
    }
  };

  // Remove active filter
  const removeFilter = (filterToRemove: string) => {
    setActiveFilters(prev => prev.filter(filter => filter !== filterToRemove));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
  };

  // Get all existing tags for the modal
  const getAllExistingTags = () => {
    return Array.from(new Set(projectNames.flatMap(pn => pn.tags))).sort();
  };

  const handleClaim = async (projectNameId: string) => {
    if (!user) return;
    
    try {
      await projectNamesService.claimProjectName(projectNameId, user.email!);
      await loadProjectNames();
    } catch (error) {
      console.error('Failed to claim project name:', error);
    }
  };

  const handleRelease = async (projectNameId: string) => {
    try {
      await projectNamesService.releaseProjectName(projectNameId);
      await loadProjectNames();
    } catch (error) {
      console.error('Failed to release project name:', error);
    }
  };

  const handleEdit = (projectName: ProjectName) => {
    setEditingProjectName(projectName);
    setShowEditModal(true);
  };

  const handleDelete = async (projectNameId: string) => {
    if (!user) return;
    
    if (window.confirm('Are you sure you want to delete this project name? It will be moved to the Deleted tab and can be restored later.')) {
      try {
        await projectNamesService.deleteProjectName(projectNameId, user.email!);
        await loadProjectNames();
      } catch (error) {
        console.error('Failed to delete project name:', error);
      }
    }
  };

  const handleRestore = async (projectNameId: string) => {
    try {
      await projectNamesService.restoreProjectName(projectNameId);
      await loadProjectNames();
    } catch (error) {
      console.error('Failed to restore project name:', error);
    }
  };

  const handlePermanentDelete = async (projectNameId: string) => {
    if (!user) return;
    
    if (window.confirm('Are you sure you want to permanently delete this project name? This action cannot be undone.')) {
      try {
        await projectNamesService.permanentlyDeleteProjectName(projectNameId);
        await loadProjectNames();
      } catch (error) {
        console.error('Failed to permanently delete project name:', error);
      }
    }
  };

  // Permission check helpers
  const canUserClaim = (projectName: ProjectName) => {
    return canClaimContent(projectName.claimedBy || '');
  };

  const canUserDelete = (projectName: ProjectName) => {
    return canDeleteContent(projectName.createdBy);
  };

  const canUserEdit = (projectName: ProjectName) => {
    return user?.email === projectName.createdBy || permissions.canManageContent;
  };

  const canUserRestore = () => {
    return permissions.canUploadContent;
  };

  const canUserPermanentDelete = () => {
    return permissions.canManageContent;
  };

  // Filter project names based on search term and active filters
  const filteredProjectNames = projectNames.filter(projectName => {
    // Apply tab filtering
    let matchesTab = false;
    if (activeTab === 'available') {
      matchesTab = !projectName.claimed && !projectName.deleted;
    } else if (activeTab === 'claimed') {
      matchesTab = projectName.claimed && !projectName.deleted;
    } else if (activeTab === 'deleted') {
      matchesTab = projectName.deleted;
    }

    if (!matchesTab) return false;

    // Apply active filters with AND logic
    const passesActiveFilters = activeFilters.every(filter => {
      const filterLower = filter.toLowerCase();
      
      // Check basic info
      const basicMatch = [
        projectName.name,
        projectName.project,
        projectName.notes,
        projectName.createdBy
      ].some(field => field.toLowerCase().includes(filterLower));
      
      // Check tags
      const tagMatch = projectName.tags.some(tag => tag.toLowerCase().includes(filterLower));
      
      return basicMatch || tagMatch;
    });
    
    if (!passesActiveFilters) return false;
    
    // Apply current search term if present
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in basic info
    const basicMatch = [
      projectName.name,
      projectName.project,
      projectName.notes,
      projectName.createdBy
    ].some(field => field.toLowerCase().includes(searchLower));
    
    // Search in tags
    const tagMatch = projectName.tags.some(tag => tag.toLowerCase().includes(searchLower));
    
    return basicMatch || tagMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading project names...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Type size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Project Names</h2>
            <p className="text-muted-foreground">{projectNames.length} project names available</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {permissions.canUploadContent && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              Add Project Name
            </button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="space-y-4">
        {/* Main Search Input */}
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search project names by name, project, tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 py-3 border rounded-lg w-full text-base"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground font-medium">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                  >
                    {filter}
                    <button
                      onClick={() => removeFilter(filter)}
                      className="hover:text-blue-600"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popular Tags */}
        {!searchTerm && activeFilters.length === 0 && projectNames.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Popular tags:</p>
            <div className="flex flex-wrap gap-2">
              {(showAllTags ? getAllTagsWithCounts() : getPopularTags()).map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => handlePopularTagClick(tag)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors flex items-center gap-1"
                >
                  {tag}
                  <span className="text-xs text-blue-500">({count})</span>
                </button>
              ))}
              {!showAllTags && getAllTagsWithCounts().length > 10 && (
                <button
                  onClick={() => setShowAllTags(true)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  See all ({getAllTagsWithCounts().length})
                </button>
              )}
              {showAllTags && (
                <button
                  onClick={() => setShowAllTags(false)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {(searchTerm || activeFilters.length > 0) && (
          <div className="text-sm text-muted-foreground">
            Found {filteredProjectNames.length} {filteredProjectNames.length === 1 ? 'project name' : 'project names'}
            {activeFilters.length > 0 && ` matching ${activeFilters.length} filter${activeFilters.length === 1 ? '' : 's'}`}
            {searchTerm && ` and search term "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('available')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Available ({tabCounts.available})
          </button>
          <button
            onClick={() => setActiveTab('claimed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'claimed'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Claimed ({tabCounts.claimed})
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deleted'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Deleted ({tabCounts.deleted})
          </button>
        </nav>
      </div>

      {/* Empty state */}
      {filteredProjectNames.length === 0 && !loading && (
        <div className="text-center py-12">
          {projectNames.length === 0 ? (
            <p className="text-muted-foreground">
              {tabCounts.available === 0 && tabCounts.claimed === 0 && tabCounts.deleted === 0
                ? "No project names added yet. Click 'Add Project Name' to get started!"
                : `No project names found in ${activeTab} category.`
              }
            </p>
          ) : (
            <div>
              <p className="text-muted-foreground">No project names found matching your filters.</p>
              {(activeFilters.length > 0 || searchTerm) && (
                <button
                  onClick={clearAllFilters}
                  className="text-primary hover:underline mt-2"
                >
                  Clear all filters to see all project names
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Project Names Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredProjectNames.map((projectName) => (
          <div key={projectName.id} className="bg-white border rounded-lg p-4 space-y-3 flex flex-col">
            {/* Project Name Header */}
            <div className="space-y-2">
              <div className="bg-muted rounded-lg flex items-center p-4">
                <h3 className="font-semibold text-base text-left">{projectName.name}</h3>
              </div>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-1">
                {projectName.tags.slice(0, 3).map((tag) => (
                  <span 
                    key={tag} 
                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs hover:bg-blue-100 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
                {projectName.tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    +{projectName.tags.length - 3} more
                  </span>
                )}
              </div>
            </div>
            
            {/* Project Details - Flexible space */}
            <div className="space-y-1 flex-1">
              <p className="text-xs text-muted-foreground">
                From: <span className="font-medium">{projectName.project}</span>
              </p>
              
              {/* Domain ownership indicator */}
              {projectName.domainOwned && (
                <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                  ✓ Domain owned
                </p>
              )}
              
              {projectName.notes && (
                <p className="text-xs text-muted-foreground italic line-clamp-2">{projectName.notes}</p>
              )}
              
              <p className="text-xs text-muted-foreground">
                By: <span className="font-medium">{projectName.createdBy}</span>
              </p>
              
              {projectName.claimed && !projectName.deleted && (
                <p className="text-xs text-orange-600 font-medium">
                  Claimed by: {projectName.claimedBy}
                </p>
              )}
              
              {projectName.deleted && (
                <p className="text-xs text-red-600 font-medium">
                  Deleted by: {projectName.deletedBy}
                </p>
              )}
            </div>

            {/* Action buttons - Compact vertical layout */}
            <div className="space-y-2">
              {!projectName.deleted ? (
                <>
                  {/* Primary Actions */}
                  <div className="flex gap-2">
                    {canUserClaim(projectName) && (
                      <button 
                        onClick={() => projectName.claimed ? handleRelease(projectName.id) : handleClaim(projectName.id)}
                        className="flex-1 text-foreground py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors border border-gray-200 hover:border-primary/40"
                      >
                        <Hand size={14} />
                        {projectName.claimed ? 'Release' : 'Claim'}
                      </button>
                    )}
                  </div>
                  
                  {/* Secondary Actions */}
                  {(canUserEdit(projectName) || canUserDelete(projectName)) && (
                    <div className="flex gap-1">
                      {canUserEdit(projectName) && (
                        <button 
                          onClick={() => handleEdit(projectName)}
                          className="flex-1 py-1 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-purple-100 hover:text-purple-600 transition-colors border border-gray-200 hover:border-purple-200 text-xs"
                        >
                          <Edit size={12} />
                          Edit
                        </button>
                      )}
                      
                      {canUserDelete(projectName) && (
                        <button 
                          onClick={() => handleDelete(projectName.id)}
                          className="flex-1 py-1 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-red-100 hover:text-red-600 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Deleted state actions */}
                  <div className="flex gap-2">
                    {canUserRestore() && (
                      <button 
                        onClick={() => handleRestore(projectName.id)}
                        className="flex-1 py-2 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-green-100 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200"
                      >
                        <RotateCcw size={14} />
                        Restore
                      </button>
                    )}
                    
                    {canUserPermanentDelete() && (
                      <button 
                        onClick={() => handlePermanentDelete(projectName.id)}
                        className="py-2 px-3 text-foreground rounded-lg flex items-center gap-1 hover:bg-red-100 hover:text-red-700 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                        title="Permanently delete (Admin/Owner only)"
                      >
                        <AlertTriangle size={12} />
                        Permanent
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Project Name Modal */}
      <AddProjectNameModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadProjectNames}
        existingTags={getAllExistingTags()}
      />

      {/* Edit Project Name Modal */}
      <EditProjectNameModal
        isOpen={showEditModal}
        projectName={editingProjectName}
        existingTags={getAllExistingTags()}
        onClose={() => {
          setShowEditModal(false);
          setEditingProjectName(null);
        }}
        onSuccess={loadProjectNames}
      />
    </div>
  );
};